import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Smartphone, Wifi, CreditCard, FileText, Key, ArrowRight, Copy, Check, Download, Shield, Clock, Headphones } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerPortalLanding = () => {
  const { tenantSlug } = useParams();
  const [tenantInfo, setTenantInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  const portalUrl = `${window.location.origin}/p/${tenantSlug}/login`;

  useEffect(() => {
    const fetchTenantInfo = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/portal/${tenantSlug}/info`);
        if (response.data.success) {
          setTenantInfo(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching tenant info:', error);
      } finally {
        setLoading(false);
      }
    };

    if (tenantSlug) {
      fetchTenantInfo();
    }
  }, [tenantSlug]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(portalUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadQR = () => {
    const svg = document.getElementById('portal-qr-code');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = 512;
      canvas.height = 512;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 56, 56, 400, 400);
      
      const link = document.createElement('a');
      link.download = `QR-Portal-${tenantName}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const primaryColor = tenantInfo?.branding?.primary_color || '#6366f1';
  const tenantName = tenantInfo?.tenant_name || 'Internet Provider';
  const companyPhone = tenantInfo?.company_phone || '';

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 border-t-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-500 dark:text-slate-400">Memuat...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      {/* Floating Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100 shadow-sm dark:border-slate-700">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {tenantInfo?.logo || tenantInfo?.branding?.logo_url ? (
                <img 
                  src={tenantInfo.logo || tenantInfo.branding?.logo_url} 
                  alt={tenantName}
                  className="h-8 sm:h-10 w-auto object-contain"
                />
              ) : (
                <div 
                  className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center text-white font-bold text-lg sm:text-xl shadow-lg"
                  style={{ 
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                    boxShadow: `0 4px 14px ${primaryColor}40`
                  }}
                >
                  {tenantName.charAt(0)}
                </div>
              )}
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-gray-900 dark:text-slate-100">{tenantName}</h1>
                <p className="text-xs text-gray-500 dark:text-slate-400">Portal Pelanggan</p>
              </div>
            </div>
            <a href={portalUrl}>
              <Button 
                size="sm"
                className="text-white shadow-lg hover:shadow-xl transition-all text-xs sm:text-sm"
                style={{ 
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)`,
                  boxShadow: `0 4px 14px ${primaryColor}40`
                }}
              >
                <span className="hidden sm:inline">Masuk Portal</span>
                <span className="sm:hidden">Masuk</span>
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-1/4 w-72 h-72 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
          <div className="text-center mb-8 sm:mb-12">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4 sm:mb-6"
              style={{ 
                backgroundColor: `${primaryColor}15`,
                color: primaryColor
              }}
            >
              <Smartphone className="w-4 h-4" />
              Self-Service Portal
            </div>
            <h2 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-3 sm:mb-4 leading-tight dark:text-slate-100">
              Kelola Akun Internet<br />
              <span style={{ color: primaryColor }}>Dengan Mudah</span>
            </h2>
            <p className="text-gray-600 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto dark:text-slate-300">
              Bayar tagihan, cek invoice, dan kelola WiFi langsung dari smartphone Anda. 
              Akses 24 jam kapanpun dibutuhkan.
            </p>
          </div>

          {/* QR Code Card */}
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl border border-gray-100 overflow-hidden max-w-3xl mx-auto dark:bg-slate-800 dark:border-slate-700">
            <div 
              className="p-4 sm:p-6 text-center"
              style={{ 
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`
              }}
            >
              <h3 className="text-white text-lg sm:text-xl font-bold">Scan QR Code</h3>
              <p className="text-white/80 text-xs sm:text-sm mt-1">Arahkan kamera HP ke QR code di bawah</p>
            </div>
            
            <div className="p-4 sm:p-8">
              <div className="flex flex-col lg:flex-row items-center justify-center gap-6 lg:gap-10">
                {/* QR Code */}
                <div className="relative">
                  <div 
                    className="absolute inset-0 rounded-2xl sm:rounded-3xl opacity-20 blur-xl"
                    style={{ backgroundColor: primaryColor }}
                  ></div>
                  <div className="relative bg-white p-4 sm:p-6 rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-100 dark:bg-slate-800 dark:border-slate-700">
                    <QRCodeSVG 
                      id="portal-qr-code"
                      value={portalUrl}
                      size={160}
                      level="H"
                      includeMargin={false}
                      fgColor="#1f2937"
                      className="sm:w-[200px] sm:h-[200px]"
                    />
                  </div>
                </div>

                {/* Divider */}
                <div className="hidden lg:flex flex-col items-center py-4">
                  <div className="w-px h-12 bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
                  <span className="text-gray-400 text-sm font-medium py-3 px-3 bg-gray-50 rounded-full dark:bg-slate-900">atau</span>
                  <div className="w-px h-12 bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
                </div>
                <div className="lg:hidden flex items-center gap-4 w-full max-w-xs">
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                  <span className="text-gray-400 text-sm font-medium">atau</span>
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                </div>

                {/* Link Section */}
                <div className="flex-1 w-full max-w-sm">
                  <p className="text-gray-500 text-sm mb-3 text-center lg:text-left dark:text-slate-400">Klik atau salin link:</p>
                  <div className="bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-200 mb-4 dark:bg-slate-900 dark:border-slate-700">
                    <a 
                      href={portalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium break-all text-xs sm:text-sm hover:underline"
                      style={{ color: primaryColor }}
                    >
                      {portalUrl}
                    </a>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      onClick={handleCopyLink}
                      variant="outline"
                      className="flex-1 h-11 rounded-xl border-2 hover:border-gray-300 transition-all"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" />
                          <span className="text-green-600 font-medium dark:text-green-400">Tersalin!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-2" />
                          <span>Salin Link</span>
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={handleDownloadQR}
                      className="flex-1 h-11 rounded-xl text-white transition-all"
                      style={{ 
                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)`
                      }}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      <span>Download QR</span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-10 sm:py-16 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 dark:text-slate-100">Fitur Portal Pelanggan</h3>
            <p className="text-gray-500 text-sm sm:text-base dark:text-slate-400">Semua kebutuhan internet Anda dalam satu aplikasi</p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {[
              { 
                icon: CreditCard, 
                title: 'Bayar Tagihan', 
                desc: 'Transfer bank atau e-wallet',
                color: '#f97316',
                bgColor: '#fff7ed'
              },
              { 
                icon: FileText, 
                title: 'Lihat Invoice', 
                desc: 'Download & cetak PDF',
                color: '#3b82f6',
                bgColor: '#eff6ff'
              },
              { 
                icon: Wifi, 
                title: 'Kelola WiFi', 
                desc: 'Ubah nama & password',
                color: '#10b981',
                bgColor: '#ecfdf5'
              },
              { 
                icon: Key, 
                title: 'Keamanan', 
                desc: 'Ganti password akun',
                color: '#8b5cf6',
                bgColor: '#f5f3ff'
              },
            ].map((feature, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group dark:bg-slate-800 dark:border-slate-700"
              >
                <div 
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mb-3 sm:mb-4 transition-transform group-hover:scale-110"
                  style={{ backgroundColor: feature.bgColor }}
                >
                  <feature.icon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: feature.color }} />
                </div>
                <h4 className="font-semibold text-gray-900 text-sm sm:text-base mb-1 dark:text-slate-100">{feature.title}</h4>
                <p className="text-gray-500 text-xs sm:text-sm dark:text-slate-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Login Section */}
      <section className="py-10 sm:py-16 px-4 sm:px-6 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 dark:text-slate-100">Cara Login</h3>
            <p className="text-gray-500 text-sm sm:text-base dark:text-slate-400">3 langkah mudah untuk mengakses portal</p>
          </div>
          
          <div className="space-y-4 sm:space-y-6">
            {[
              {
                step: '1',
                title: 'Scan QR Code',
                desc: 'Buka kamera HP dan arahkan ke QR code di atas. Link akan terbuka otomatis.'
              },
              {
                step: '2',
                title: 'Masukkan ID Pelanggan',
                desc: 'ID Pelanggan tertera pada invoice atau kontrak berlangganan Anda.'
              },
              {
                step: '3',
                title: 'Masukkan Password',
                desc: (
                  <>
                    Password default: <code className="bg-gray-900 text-white px-2 py-1 rounded text-xs font-mono">12345678</code>
                    <span className="text-orange-600 text-xs block mt-1 dark:text-orange-400">⚠️ Segera ubah password setelah login pertama</span>
                  </>
                )
              }
            ].map((item, idx) => (
              <div 
                key={idx}
                className="flex items-start gap-4 sm:gap-5 bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100 shadow-sm dark:bg-slate-800 dark:border-slate-700"
              >
                <div 
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg sm:text-xl shrink-0 shadow-lg"
                  style={{ 
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)`,
                    boxShadow: `0 4px 14px ${primaryColor}30`
                  }}
                >
                  {item.step}
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm sm:text-base mb-1 dark:text-slate-100">{item.title}</h4>
                  <p className="text-gray-500 text-xs sm:text-sm leading-relaxed dark:text-slate-400">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-3 gap-4 sm:gap-8 text-center">
            {[
              { icon: Shield, label: 'Aman & Terenkripsi' },
              { icon: Clock, label: 'Akses 24 Jam' },
              { icon: Headphones, label: 'Dukungan Pelanggan' },
            ].map((item, idx) => (
              <div key={idx} className="flex flex-col items-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gray-100 flex items-center justify-center mb-2 dark:bg-slate-800">
                  <item.icon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600 dark:text-slate-300" />
                </div>
                <span className="text-gray-600 text-xs sm:text-sm font-medium dark:text-slate-300">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <div 
            className="rounded-2xl sm:rounded-3xl p-6 sm:p-10 text-center text-white relative overflow-hidden"
            style={{ 
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`
            }}
          >
            {/* Decorative circles */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
            
            <div className="relative">
              <h3 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3">Siap Mengakses Portal?</h3>
              <p className="text-white/80 text-sm sm:text-base mb-5 sm:mb-6">Kelola akun internet Anda sekarang dengan mudah</p>
              <a href={portalUrl}>
                <Button 
                  size="lg"
                  className="bg-white hover:bg-gray-100 text-gray-900 px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base rounded-xl shadow-xl hover:shadow-2xl transition-all font-semibold dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-100"
                >
                  Masuk ke Portal
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 sm:py-8 px-4 sm:px-6 border-t border-gray-100 dark:border-slate-700">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            {tenantInfo?.logo || tenantInfo?.branding?.logo_url ? (
              <img 
                src={tenantInfo.logo || tenantInfo.branding?.logo_url} 
                alt={tenantName}
                className="h-6 sm:h-8 w-auto object-contain opacity-60"
              />
            ) : (
              <div 
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm opacity-80"
                style={{ backgroundColor: primaryColor }}
              >
                {tenantName.charAt(0)}
              </div>
            )}
            <span className="text-gray-400 font-medium text-sm sm:text-base">{tenantName}</span>
          </div>
          <p className="text-gray-400 text-xs sm:text-sm">
            © {new Date().getFullYear()} {tenantName}. All rights reserved.
          </p>
          {companyPhone && (
            <p className="text-gray-400 text-xs sm:text-sm mt-2">
              Butuh bantuan? Hubungi <a href={`tel:${companyPhone}`} className="underline hover:text-gray-600">{companyPhone}</a>
            </p>
          )}
        </div>
      </footer>
    </div>
  );
};

export default CustomerPortalLanding;
